/**
 * 线上模式输出兜底清洗：删除 AI 在【线上模式】下抽风输出的 [动作描写]（连方括号带内容一起删）。
 *
 * 背景：
 * - 线下模式要求 AI 用 [xxx] 包裹动作/环境/心理描写；切回线上后历史里仍残留这些 [xxx]。
 * - 主防线是线上模式 prompt（buildOnlineModeSection）约束 AI 不再模仿，但弱模型可能仍被历史带跑。
 * - 本模块作为输出侧的最后兜底：仅在【发送前】对线上模式的本轮回复生效，不触碰历史记录。
 *
 * 与已删除的 useOfflineHistorySanitizer 的区别：
 * - 那个洗的是「发给 LLM 的历史副本」，且只删整行方括号、删空后留占位文本。
 * - 这个洗的是「AI 当下吐出的新回复」，整行与行内混合都删，且连括号内容一并删除、不留占位。
 */

/**
 * 删除文本中的 [描写] 片段（含方括号本身）。
 * - 整行仅 [xxx]：删后该行变空
 * - 行内混合 [xxx] 文字：删 [xxx] 段，保留文字
 * - 保护 Markdown 链接 [文字](url)：方括号后紧跟 ( 的不删
 */
/**
 * 判断方括号片段是否为「动作描写」：内部含至少一个 CJK 汉字。
 * 纯英数/符号内容（如 [doge]、[1,2,3]）不算描写，予以保留。
 */
function isCjkBracket(inner: string): boolean {
  // 逐字符判断，避免在正则里写复杂字符类导致 ReDoS / 难读范围
  for (const ch of inner) {
    const code = ch.codePointAt(0) ?? 0
    if (code >= 0x4E00 && code <= 0x9FFF)
      return true
  }
  return false
}

export function stripBracketDescriptions(text: string): string {
  // 匹配「不跨行、不嵌套」的方括号片段，判定与保护逻辑放到回调里（规避正则回溯风险）：
  // - 前面紧跟标识符字符（A-Za-z0-9_$）的，视为 arr[0]、f[x] 这类下标/数学式，保留
  // - 紧跟 ( 的，视为 [text](url) 形式的 Markdown 链接，保留
  // - 括号内不含 CJK 汉字的（如 [doge]、[1,2,3]），视为非描写，保留
  const stripped = text.replace(/\[[^[\]\n]*\]\(?/g, (match, offset: number, full: string) => {
    if (match.endsWith('(')) // Markdown 链接 [text](
      return match
    const prev = offset > 0 ? full[offset - 1] : ''
    if (/[\w$]/.test(prev)) // 代码下标 / 数学式
      return match
    const inner = match.slice(1, -1)
    return isCjkBracket(inner) ? '' : match
  })
  return stripped
    .replace(/[ \t]+/g, ' ') // 合并删除后产生的多余空格
    .replace(/\n{3,}/g, '\n\n') // 折叠多余空行
    .replace(/^[ \t]+|[ \t]+$/gm, '') // 去每行首尾空白
    .trim()
}

/** 可被清洗的解析结果最小结构（processAIResponse 的返回值子集） */
export interface BracketSanitizable {
  text?: string
  messages?: Array<{ text?: string }>
}

/**
 * 就地清洗解析结果中的 [描写]，用于线上模式输出兜底。
 * - 多条消息：逐条清洗，删空的条目剔除；若全部删空则保留原数组（避免出现「AI 完全没回应」）
 * - 单条 text：清洗；若删空则保留原文（同上兜底）
 */
export function stripBracketDescriptionsFromResponse(parsed: BracketSanitizable): void {
  if (Array.isArray(parsed.messages) && parsed.messages.length > 0) {
    const cleaned = parsed.messages
      .map((m) => {
        if (typeof m.text !== 'string')
          return m
        return { ...m, text: stripBracketDescriptions(m.text) }
      })
      .filter(m => typeof m.text !== 'string' || m.text.length > 0)

    // 兜底：全部被删空时保留原数组，不让用户收到空回复
    if (cleaned.length > 0)
      parsed.messages = cleaned
  }

  if (typeof parsed.text === 'string' && parsed.text.length > 0) {
    const cleaned = stripBracketDescriptions(parsed.text)
    // 兜底：删空则保留原文
    if (cleaned.length > 0)
      parsed.text = cleaned
  }
}
